import React, { useState, useEffect, useRef, useCallback } from "react";
import cytoscape from "cytoscape";
import ReactLoading from "react-loading";
import chevron_down from "../assets/chevron-down.svg";
import chevron_up from "../assets/chevron-up.svg";
import Img from "./Img";

import infoCircle from "../assets/infoCircle.svg";
import download from "../assets/download-logo.svg";
import coffee from "../assets/coffee.svg";

import interactionSampleFile from "../assets/test_demo/interaction.xlsx";
import modelSampleFile from "../assets/test_demo/model.xlsx";

import { Link } from "react-router-dom";
import * as XLSX from "xlsx";
import Header from "./Header";

// API Configuration
const API_BASE = "/api";

// Constants
const KIND_DICT = {
  "strong corroboration": 2,
  "empty attribute": 1,
  "indirect interaction": 3,
  "path corroboration": 5,
  specification: 7,
  "hanging extension": 40,
  "full extension": 39,
  "internal extension": 38,
  "dir contradiction": 11,
  "sign contradiction": 10,
  "att contradiction": 9,
  "dir mismatch": 20,
  "path mismatch": 19,
  "self-regulation": 18,
};

const REVERSE_KIND_DICT = {
  2: "strong corroboration",
  1: "empty attribute",
  3: "indirect interaction",
  5: "path corroboration",
  7: "specification",
  40: "hanging extension",
  39: "full extension",
  38: "internal extension",
  11: "dir contradiction",
  10: "sign contradiction",
  9: "att contradiction",
  20: "dir mismatch",
  19: "path mismatch",
  18: "self-regulation",
};

const MATCH_DICT = {
  "source present": 1,
  "target present": 100,
  "both present": 10,
  "neither present": 0.1,
};

const ATT_DEFAULT = [];

const NAME_SIM_METRICS = [
  { value: "jaccard", label: "Jaccard" },
  { value: "edit_sim", label: "Edit_sim" },
  { value: "grounding", label: "Grounding" },
];

const NAME_SIM_METRICS_WITH_NONE = [
  { value: "none", label: "None" },
  { value: "jaccard", label: "Jaccard" },
  { value: "edit_sim", label: "Edit_sim" },
  { value: "grounding", label: "Grounding" },
];

// --- Node grey-shade mapping (for network rendering) ---
const GREY_SHADES = [
  "#cccccc", // lightest
  "#b0b0b0",
  "#949494",
  "#787878",
  "#5c5c5c",
  "#404040", // darkest
];

// Used only when a node has no type at all.
const DEFAULT_NODE_COLOR = "#666";

const normalizeType = (s) => String(s ?? "").trim().toLowerCase();

// Deterministic hash so a given type always maps to the same shade.
const hashString = (s) => {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (h * 31 + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
};

const nodeColor = (type) => {
  const key = normalizeType(type);
  if (!key) return DEFAULT_NODE_COLOR;
  return GREY_SHADES[hashString(key) % GREY_SHADES.length];
};

// Distinct element types present in the model, in first-seen order (for the legend).
const entityTypesInModel = (model) => {
  if (!Array.isArray(model)) return [];
  const seen = new Map(); // normalized -> original label
  for (const row of model) {
    const raw = row?.["Element Type"];
    const key = normalizeType(raw);
    if (key && !seen.has(key)) seen.set(key, String(raw).trim());
  }
  return Array.from(seen.values());
};

// Styles
const styles = `
 .web_container {
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding-bottom:16px;
  font-family: Arial, sans-serif;
    min-height: calc(100vh - 64px);

  max-width: 100%;
  overflow: auto;
  box-sizing: border-box;
}

.main-content {
  display: flex;
  flex-direction: row;
  gap: 20px;
  flex: 1;
  min-height: 0;
  min-width: 0;
  overflow: hidden;
  padding:0 16px;
}

.left_container {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
  min-height: 0;
}

.introduce_window {
  flex: 0 0 50px;
  overflow-y: auto;
  background: #fafafa;
  padding: 8px 16px;
  border-radius: 8px;
}

.introduce_window h1 {
  color: #333;
  margin: 0 0 15px 0;
}

.introduce_window ul {
  list-style-type: disc;
  padding-left: 16px;
}

.introduce_window li {
  margin: 8px 0;
  line-height: 1.5;
}

.table_window {
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
    height: 0;
  display: flex;
  flex-direction: column;
  background: #fff;
  padding: 15px;
  border: 1px solid #ddd;
  border-radius: 8px;
}

.controller_container {
  flex: 0 0 320px;
  display: flex;
  gap: 20px;
  min-height: 0;
}

.controller_window {
  display: flex;
  flex-direction: column;
  gap: 10px;
  // overflow-y: auto;
  background-color:#fff;
  border-radius:10px;
  border:2px solid #ccc;
  padding:10px;
  
}

.interaction_window {
  flex: 1;
  background: #fff;
  padding: 15px;
  border: 1px solid #ddd;
  border-radius: 8px;
  min-height: 0;
  overflow-y: auto;
}

.graph_container {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 15px;
  min-width: 0;
  min-height: 0;
  margin-right:16px;
}

.graph_window {
  flex: 1;
  min-height: 0;
  border: 2px solid #ccc;
  border-radius: 8px;
  background: #fafafa;
  position: relative;
}

button {
  padding: 8px 10px;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s;
}

.classify_button {
  color: #00205B;
  font-weight: bold;
  min-width:200px;
  background: #FFB81C;
}

.classify_button:hover {
  background: #00205B;
  color: #FFB81C;
}
.download_button {
  color: #fff;
  font-weight: bold;
  background-color:  #036b45;
    
}

.download_button:hover {
  background: #00AD6E;
  
}

.corroboration_button {
  background: #c8daf7;
  color: #333;
  min-width:95px;
}

.corroboration_button:hover {
  background: #7198bc;
}

.contradiction_button {
  background: #fdeeb4;
  color: #333;
  min-width:95px;
}

.contradiction_button:hover {
  background: #e6e68a;
}

.flagged_button {
  background: #ee4739;
  color: white;
}

.flagged_button:hover {
  background: #d63a2e;
}

.extension_button {
  background: #cbe3c4;
  color: #000;
}

.extension_button:hover {
  background: #0fa51c;
}

.reset_button {
  background: #770538;
  color: #fff;
  font-weight:bold;
  min-width: 95px;
}

.reset_button:hover {
  color: white;
  background: #FF5B45;
}

.dropbtn {
  background: #00205B;
  color: #fff;
  width: 100%;
}

.dropbtn:hover {
  background: #003594;
  color: #fff;
}

.dropdown {
  position: relative;
  display: inline-block;
  width: 100%;
}

.dropdown-content {
  display: none;
  position: absolute;
  background-color: #f9f9f9;
  width: 100%;
  box-shadow: 0px 8px 16px 0px rgba(0,0,0,0.2);
  z-index: 1;
  border-radius: 5px;
}

.dropdown-content.show {
  display: block;
  display: grid;
  grid-template-columns: 1fr 1fr;
  padding:4px 0px 8px 0px;

}

.dropdown-content a {
  color: black;
  padding: 12px 16px;
  text-decoration: none;
  display: block;
  cursor: pointer;
}

.dropdown-content a:hover {
  background-color: #f1f1f1;
}

input[type="file"] {
  margin-bottom: 10px;
}

/* ===== Virtualized Table Styles ===== */
.virtual-table-wrapper {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}

.virtual-table-toolbar {
  margin-bottom: 10px;
  flex-shrink: 0;
}

.virtual-table-header-scroll {
  overflow-x: auto;
  overflow-y: hidden;
  flex-shrink: 0;
  border: 2px solid #423f3f;
  border-bottom: none;
}

.virtual-table-body-scroll {
  flex: 1;
  min-height: 0;
  border: 2px solid #423f3f;
  border-top: 1px solid #e5e7eb;
  overflow-y: auto;   
  overflow-x: auto;

}

.virtual-table-header-row {
  display: flex;
  background-color: #f9fafb;
}

.virtual-table-header-cell {
  flex-shrink: 0;
  padding: 4px 8px;
  text-align: left;
  font-size: 10px;
  font-weight: 600;
  color: #000;
  letter-spacing: 0.05em;
  border-right: 1px solid #e5e7eb;
  line-height: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.virtual-table-header-cell input {
  padding: 2px 4px;
  font-size: 9px;
  width: 100%;
  box-sizing: border-box;
}

.virtual-table-row {
  display: flex;
  cursor: pointer;
  border-bottom: 1px solid #f0f0f0;
  box-sizing: border-box;
}

.virtual-table-row:hover {
  background-color: #f5f5f5;
}

.virtual-table-row.selected {
  background-color: #b3d9ff;
  border-left: 4px solid #1976d2;
}

.virtual-table-cell {
  flex-shrink: 0;
  padding: 2px 6px;
  font-size: 10px;
  color: #111827;
  border-right: 1px solid #e5e7eb;
  line-height: 1;
  height: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  box-sizing: border-box;
  display: flex;
  align-items: center;
}

/* Legacy table styles kept for compatibility */
.tableContainer {
  margin-top: 15px;
  overflow: auto;
  height: 100vh;
  width: 100%;
  border: 2px solid #423f3f;
}

.tableContainer table {
  width: 100%;
  border-collapse: collapse;
  font-size: 10px;
  border: 2px solid #e5e7eb;
}

.tableContainer table td {
  padding: 2px 6px;
  font-size: 10px;
  color: #111827;
  border-right: 1px solid #e5e7eb;
  line-height: 1;
  height: 20px;
  max-height: 20px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tableContainer table th {
  background-color: #f9fafb;
  position: sticky;
  top: 0;
  z-index: 10;
  padding: 4px 8px;
  text-align: left;
  font-size: 10px;
  font-weight: 600;
  color: #000;
  letter-spacing: 0.05em;
  border-right: 1px solid #e5e7eb;
  line-height: 1;
  height: 24px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tableContainer table tr:hover {
  background-color: #f5f5f5;
  cursor:pointer;
}

.tableContainer table tr.selected {
  background-color: #b3d9ff;
  border-left: 4px solid #1976d2;
}

#interaction_info p {
  margin: 8px 0;
}

#entity {
  font-weight: bold;
  color: #333;
}

.corroboration_marker {
  background-color: #c8daf7;
  color: #000 !important;
  padding: 2px 8px;
  border-radius: 3px;
}

.contradiction_marker {
  background-color: #fdeeb4;
  color: #333;
  padding: 2px 8px;
  border-radius: 3px;
}

.flagged_marker {
  background-color: #ee827d;
  color: #000;
  padding: 2px 8px;
  border-radius: 3px;
}

.extension_marker {
  background-color: #cbe3c4;
  color: #000 !important;
  padding: 2px 8px;
  border-radius: 3px;
}

.checkbox-item {
  display: flex;
  align-items: center;
  padding: 8px;
  cursor: pointer;
  font-size:12px;
}

.checkbox-item:hover {
  background-color: #f1f1f1;
}

.checkbox-item input[type="checkbox"] {
  margin-right: 8px;
  cursor: pointer;
}

.checkbox-item span {
  cursor: pointer;
}

.button-wrapper {
  position: relative;
  display: flex;
  flex: 1;
}

.button-wrapper button {
  width: 100%;
}

.disabled-button {
  opacity: 0.5;
  cursor: not-allowed !important;
  filter: grayscale(50%);
}

.tooltip {
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-bottom: 8px;
  padding: 6px 12px;
  background-color: #333;
  color: white;
  border-radius: 4px;
  font-size: 14px;
  white-space: nowrap;
  z-index: 9000;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.tooltip-download-button{
  min-width:100px;
  white-space: normal;
}

.tooltip::after {
  content: "";
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  border: 6px solid transparent;
  border-top-color: #333;
}

.loading{
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 70%;
}

.circle {
  width: 50px;
  height: 50px;
  font-size:40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  text-align: center;
  font-size: 12px;
  word-wrap: break-word;
  padding: 5px;
  margin:0 8px;
}

.circle-corroboration{
  background-color: #c8daf7;
}

.circle-flagged{
  background-color: #ee827d;
}

.circle-contradiction{
  background-color: #fdeeb4;
}

.circle-extension{
  background-color: #cbe3c4;
}

#visualization{
  display:flex ;
  align-items:center;
}

.upload-container{
  margin-top:10px;
  background-color: #DBEEFF;
  padding:12px;
  border-radius:8px 8px 0 0;
  color:#fff;
  display:flex;
  justify-content:space-between;
  align-items:center;
  color: #102751;
  border:2px dashed #102751;
}
.upload-container button{
  border:2px solid #102751;
  background:#fff
}

.melody-logo{
  width:50px;
  height:50px;
  background-color: #194089;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 100%;
  margin-right:10px;
}

.melody-container{
  display:flex;
  align-items:center;
}

.melody-container h1{
  font-size:45px;
  color: #102751;
  margin:0;
  font-family: 'Mystery Quest', cursive;
}

.violin-tooltip {
  position: absolute;
  top: 2px;
  left: 200px;
  background-color: white;
  border: 2px solid #102751;
  border-radius: 8px;
  padding: 15px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  min-width: 550px;
  max-width: 650px;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.3s ease, visibility 0.3s ease;
  pointer-events: none;
}

.melody-logo:hover .violin-tooltip {
  opacity: 0.9;
  visibility: visible;
}

.violin-tooltip h4 {
  margin: 0 0 10px 0;
  color: #102751;
  font-size: 16px;
}

.violin-tooltip ul {
  list-style-type: disc;
  padding-left: 20px;
  margin: 0;
}

.violin-tooltip li {
  margin: 8px 0;
  line-height: 1.5;
  font-size: 14px;
  color: #333;
}

.melody-container {
  display: flex;
  align-items: center;
  position: relative;
}

.error-message {
  background-color: #fee;
  border: 1px solid #fcc;
  border-radius: 4px;
  padding: 12px;
  margin: 10px 0;
  color: #c33;
  display: flex;
  align-items: center;
  gap: 8px;
}

.error-message-close {
  margin-left: auto;
  cursor: pointer;
  font-weight: bold;
}

.error-banner {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 10000;
  max-width: 400px;
  background-color: #fee;
  border: 2px solid #c33;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  animation: slideIn 0.3s ease-out;
}

@keyframes slideIn {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

.error-banner-content {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.error-banner-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: bold;
  color: #c33;
}

.error-banner-message {
  color: #666;
  font-size: 14px;
}

.error-banner-close {
  cursor: pointer;
  font-size: 20px;
  color: #999;
}

.error-banner-close:hover {
  color: #333;
}

.biorecipe-container{
  display:flex;
  align-items:center;
  background-color: #efefef;
  padding:4px;
  border-radius:10px;
}

.biorecipe-container a {
  color: #102751;
  margin: 0 4px;
  font-size: 12px;
  transition: opacity 0.3s;
}

.biorecipe-container a:hover {
  opacity: 0.8;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #999;
  text-align: center;
  padding: 40px 20px;
  user-select: none;
}

.empty-state-icon {
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.4;
}

.empty-state-title {
  font-size: 16px;
  font-weight: 600;
  color: #666;
  margin: 0 0 8px 0;
}

.empty-state-subtitle {
  font-size: 13px;
  color: #aaa;
  margin: 0;
  max-width: 280px;
  line-height: 1.5;
}

/* ===== Name Match Config Styles ===== */
.name-match-section {
  grid-column: 1 / -1;
  border-top: 1px solid #ddd;
  padding: 10px 8px 4px 8px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.name-match-section-title {
  font-size: 11px;
  font-weight: 700;
  color: #00205B;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0;
}

.name-match-row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
}

.name-match-row label {
  font-size: 12px;
  color: #333;
  white-space: nowrap;
  min-width: 70px;
}

.name-match-toggle {
  position: relative;
  width: 30px;
  height: 20px;
  border-radius: 10px;
  cursor: pointer;
  border: none;
  padding: 0;
  transition: background-color 0.2s;
}

.name-match-toggle.active {
  background-color: #00205B;
}

.name-match-toggle.inactive {
  background-color: #ccc;
}

.name-match-toggle-knob {
  position: absolute;
  top: 2px;
  width: 16px;
  height: 16px;
  background: #fff;
  border-radius: 50%;
  box-shadow: 0 1px 3px rgba(0,0,0,0.2);
  transition: left 0.2s;
}

.name-match-toggle.active .name-match-toggle-knob {
  left: 12px;
}

.name-match-toggle.inactive .name-match-toggle-knob {
  left: 2px;
}

.name-match-slider {
  -webkit-appearance: none;
  appearance: none;
  width: 80px;
  height: 4px;
  border-radius: 2px;
  background: #ddd;
  outline: none;
  cursor: pointer;
}

.name-match-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: #00205B;
  cursor: pointer;
}

.name-match-slider::-moz-range-thumb {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: #00205B;
  cursor: pointer;
  border: none;
}

.name-match-threshold-value {
  font-family: monospace;
  font-weight: 700;
  color: #00205B;
  font-size: 12px;
  min-width: 32px;
  text-align: right;
}

.name-match-metric-select {
  padding: 4px 8px;
  font-size: 12px;
  border: 1px solid #ccc;
  border-radius: 4px;
  background: #fff;
  color: #333;
  cursor: pointer;
  flex: 1;
}

.name-match-metric-select:focus {
  outline: none;
  border-color: #00205B;
}

.name-match-disabled-text {
  font-size: 11px;
  color: #999;
  font-style: italic;
}
`;

// =============================================
// Error Banner Component
// =============================================
const ErrorBanner = ({ error, onClose }) => {
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        onClose();
      }, 8000);
      return () => clearTimeout(timer);
    }
  }, [error, onClose]);

  if (!error) return null;

  return (
    <div className="error-banner">
      <div className="error-banner-content">
        <div className="error-banner-header">
          <span>⚠️ Error</span>
          <span className="error-banner-close" onClick={onClose}>
            ×
          </span>
        </div>
        <div className="error-banner-message">{error}</div>
      </div>
    </div>
  );
};

// =============================================
// UploadButton Component
// =============================================
// Add sampleFilePath to UploadButton props
const UploadButton = ({
  postAccess,
  onUploadComplete,
  handleLoading,
  file,
  setFile,
  onError,
  hasBioLink = false,
  title,
  sampleFilePath, // ← NEW
}) => {
  const serverUrl = `${API_BASE}/${postAccess}/`;
  const fileInputRef = useRef(null);
  const [selectedFileName, setSelectedFileName] = useState(null);
  const [loadingSample, setLoadingSample] = useState(false); // ← NEW

  const uploadFile = async (selectedFile) => {
    handleLoading(true);
    const formData = new FormData();
    formData.append("file", selectedFile);

    try {
      const response = await fetch(serverUrl, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = `Upload failed (Status: ${response.status})`;
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage =
            errorJson.detail ||
            errorJson.error ||
            errorJson.message ||
            errorMessage;
        } catch {
          if (errorText) errorMessage = errorText;
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      if (!result || typeof result !== "object")
        throw new Error("Invalid response from server");
      onUploadComplete(result, postAccess);
    } catch (error) {
      handleLoading(false);
      console.error("Error uploading file:", error);
      if (
        error.name === "TypeError" &&
        error.message.includes("Failed to fetch")
      ) {
        onError(
          "Network error: Unable to connect to server. Please check your connection and try again.",
        );
      } else {
        onError(error.message || "Failed to upload file. Please try again.");
      }
    }
  };

  // ← NEW: fetch the static sample file and pipe through the same uploadFile flow
const handleLoadSample = async () => {
  if (!sampleFilePath) return;
  setLoadingSample(true);
  try {
    const res = await fetch(sampleFilePath);
    if (!res.ok) throw new Error(`Could not fetch sample file (${res.status})`);
    const blob = await res.blob();
    
    // BEFORE:
    // const fileName = sampleFilePath.split("/").pop();
    
    // AFTER — strip the content hash [xxxxxxxx] from the filename:
    const rawName = sampleFilePath.split("/").pop();
    const fileName = rawName.replace(/\.[a-f0-9]{8,}\.(xlsx|xls)$/i, ".$1");
    
    const sampleFile = new File([blob], fileName, {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    setSelectedFileName(fileName);   // ← now shows "interaction.xlsx"
    await uploadFile(sampleFile);
  } catch (error) {
    onError(error.message || "Failed to load sample file.");
  } finally {
    setLoadingSample(false);
  }
};
  const handleFileChange = (event) => {
    try {
      const selectedFile = event.target.files[0];
      if (selectedFile) {
        const validExtensions = [".xlsx", ".xls"];
        const fileExtension = selectedFile.name
          .toLowerCase()
          .slice(selectedFile.name.lastIndexOf("."));
        if (!validExtensions.includes(fileExtension)) {
          onError(
            "Invalid file type. Please upload an Excel file (.xlsx or .xls)",
          );
          event.target.value = "";
          return;
        }
        const maxSize = 50 * 1024 * 1024;
        if (selectedFile.size > maxSize) {
          onError("File too large. Maximum file size is 50MB");
          event.target.value = "";
          return;
        }
        setFile(selectedFile);
        setSelectedFileName(selectedFile.name);
        uploadFile(selectedFile);
      }
    } catch (error) {
      console.error("Error selecting file:", error);
      onError("Failed to select file. Please try again.");
    }
  };

  return (
    <div className="upload-container">
      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <div style={{ color: "#12296c", fontSize: "24px" }}>{title}</div>
        {hasBioLink && (
          <div className="biorecipe-container">
            <Img src={infoCircle} width="12px" />
            <Link to="/biorecipe" rel="noopener noreferrer">
              BioRECIPE
            </Link>
          </div>
        )}
      </div>
      <input
        type="file"
        accept=".xlsx,.xls"
        onChange={handleFileChange}
        ref={fileInputRef}
        style={{ display: "none" }}
      />
      {/* ← NEW: button group */}
      <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
        <button onClick={() => fileInputRef.current?.click()}>
          {selectedFileName
            ? `Re-upload (${selectedFileName})`
            : "Choose File & Upload"}
        </button>
        {sampleFilePath && (
          <button
            onClick={handleLoadSample}
            disabled={loadingSample}
            style={{
              background: "#FFDECD",
              color: "#00205B",
              border: "1px solid #00205B",
              fontSize: "12px",
              opacity: loadingSample ? 0.6 : 1,
              cursor: loadingSample ? "not-allowed" : "pointer",
              whiteSpace: "nowrap",
            }}
          >
            {loadingSample ? (
              "Loading…"
            ) : (
              <div style={{display:'flex',alignItems:'center'}}>
                <Img src={coffee} />
                <div>{" Try Demo"}</div>
              </div>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

// =============================================
// ChooseAttr Component
// =============================================
const ChooseAttr = ({ onAttributeChange, isDisabled }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedAttrs, setSelectedAttrs] = useState([]);
  const [showTooltip, setShowTooltip] = useState(false);

  // Name matching config state
  const [thresholdEnabled, setThresholdEnabled] = useState(false);
  const [threshold, setThreshold] = useState(0.8);
  const [nameSimMetric, setNameSimMetric] = useState("none");

  // Separate boolean for Element Type → differ_gene_type
  const [differGeneType, setDifferGeneType] = useState(true);

  const toggleDropdown = () => {
    setShowDropdown(!showDropdown);
  };

  // Helper to build the config object passed to the parent
  const buildConfig = (overrides = {}) => ({
    name_match_threshold: thresholdEnabled ? threshold : null,
    name_sim_metric: nameSimMetric,
    differ_gene_type: differGeneType,
    ...overrides,
  });

  const toggleAttribute = (...attrs) => {
    try {
      let newAttrs;
      const isSelected = attrs.some((attr) => selectedAttrs.includes(attr));

      if (isSelected) {
        newAttrs = selectedAttrs.filter((attr) => !attrs.includes(attr));
      } else {
        newAttrs = [...selectedAttrs, ...attrs];
      }

      setSelectedAttrs(newAttrs);
      onAttributeChange(newAttrs, buildConfig());
    } catch (error) {
      console.error("Error toggling attribute:", error);
    }
  };

  const handleDifferGeneTypeToggle = () => {
    const newVal = !differGeneType;
    setDifferGeneType(newVal);
    onAttributeChange(selectedAttrs, buildConfig({ differ_gene_type: newVal }));
  };

  const handleThresholdToggle = () => {
    const newEnabled = !thresholdEnabled;
    setThresholdEnabled(newEnabled);
    // When enabling threshold, default metric to "jaccard"; when disabling, default to "none"
    const newMetric = newEnabled ? "jaccard" : "none";
    setNameSimMetric(newMetric);
    onAttributeChange(
      selectedAttrs,
      buildConfig({
        name_match_threshold: newEnabled ? threshold : null,
        name_sim_metric: newMetric,
      }),
    );
  };

  const handleThresholdChange = (e) => {
    const val = parseFloat(e.target.value);
    setThreshold(val);
    onAttributeChange(
      selectedAttrs,
      buildConfig({
        name_match_threshold: thresholdEnabled ? val : null,
      }),
    );
  };

  const handleMetricChange = (e) => {
    const val = e.target.value;
    setNameSimMetric(val);
    onAttributeChange(
      selectedAttrs,
      buildConfig({
        name_sim_metric: val,
      }),
    );
  };

  const isChecked = (...attrs) => {
    return attrs.some((attr) => selectedAttrs.includes(attr));
  };

  return (
    <div
      className="dropdown"
      onMouseEnter={() => isDisabled && setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <button
        onClick={toggleDropdown}
        className={isDisabled ? "disabled-button dropbtn" : "dropbtn"}
        style={{
          display: "flex",
          alignItems: "center",
          padding: "16px 4px",
          justifyContent: "space-between",
        }}
        disabled={isDisabled}
      >
        <div style={{ margin: "auto", fontWeight: "bold" }}>
          Choose attributes to match
        </div>
        {showDropdown ? (
          <Img src={chevron_up} width={"15px"} />
        ) : (
          <Img src={chevron_down} width={"15px"} />
        )}
      </button>
      <div
        className={`dropdown-content ${showDropdown ? "show" : ""}`}
        onClick={(e) => e.stopPropagation()}
      >
        <label className="checkbox-item">
          <input
            type="checkbox"
            checked={differGeneType}
            onChange={handleDifferGeneTypeToggle}
          />
          <span>Element Type</span>
        </label>
        <label className="checkbox-item">
          <input
            type="checkbox"
            checked={isChecked(
              "Regulated Compartment",
              "Regulator Compartment",
            )}
            onChange={() =>
              toggleAttribute("Regulated Compartment", "Regulator Compartment")
            }
          />
          <span>Compartment</span>
        </label>
        <label className="checkbox-item">
          <input
            type="checkbox"
            checked={isChecked(
              "Regulated Compartment ID",
              "Regulator Compartment ID",
            )}
            onChange={() =>
              toggleAttribute(
                "Regulated Compartment ID",
                "Regulator Compartment ID",
              )
            }
          />
          <span>Compartment ID</span>
        </label>
        <label className="checkbox-item">
          <input
            type="checkbox"
            checked={isChecked("Mechanism")}
            onChange={() => toggleAttribute("Mechanism")}
          />
          <span>Mechanism</span>
        </label>
        <label className="checkbox-item">
          <input
            type="checkbox"
            checked={isChecked("Cell Line")}
            onChange={() => toggleAttribute("Cell Line")}
          />
          <span>Cell Line</span>
        </label>

        <label className="checkbox-item">
          <input
            type="checkbox"
            checked={isChecked("Cell Type")}
            onChange={() => toggleAttribute("Cell Type")}
          />
          <span>Cell Type</span>
        </label>
        <label className="checkbox-item">
          <input
            type="checkbox"
            checked={isChecked("Tissue Type")}
            onChange={() => toggleAttribute("Tissue Type")}
          />
          <span>Tissue Type</span>
        </label>
        <label className="checkbox-item">
          <input
            type="checkbox"
            checked={isChecked("Organism")}
            onChange={() => toggleAttribute("Organism")}
          />
          <span>Organism</span>
        </label>

        {/* ===== Name Matching Configuration ===== */}
        <div className="name-match-section">
          <p className="name-match-section-title">Name Matching</p>

          {/* Threshold toggle + slider */}
          <div className="name-match-row">
            <label>Threshold</label>
            <button
              type="button"
              className={`name-match-toggle ${thresholdEnabled ? "active" : "inactive"}`}
              onClick={handleThresholdToggle}
              title={
                thresholdEnabled ? "Disable threshold" : "Enable threshold"
              }
            >
              <span className="name-match-toggle-knob" />
            </button>
            {thresholdEnabled ? (
              <>
                <input
                  type="range"
                  className="name-match-slider"
                  min="0"
                  max="1"
                  step="0.01"
                  value={threshold}
                  onChange={handleThresholdChange}
                />
                <span className="name-match-threshold-value">
                  {threshold.toFixed(2)}
                </span>
              </>
            ) : (
              <span className="name-match-disabled-text">None</span>
            )}
          </div>

          {/* Similarity metric dropdown */}
          <div className="name-match-row">
            <label>Metric</label>
            <select
              className="name-match-metric-select"
              value={nameSimMetric}
              onChange={handleMetricChange}
            >
              {(thresholdEnabled
                ? NAME_SIM_METRICS
                : NAME_SIM_METRICS_WITH_NONE
              ).map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
      {showTooltip && isDisabled && (
        <div className="tooltip">Please upload files first</div>
      )}
    </div>
  );
};

// =============================================
// ClassificationButton Component
// =============================================
const ClassificationButton = ({
  userData,
  onClassifyComplete,
  isDisabled,
  handleLoading,
  onError,
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const serverUrlClassify = `${API_BASE}/classify/`;

  const handleClassification = async () => {
    console.log("VIOLIN classification...");

    if (!userData.reading || !userData.model) {
      onError("Please upload both reading and model files first");
      return;
    }

    handleLoading(true);

    try {
      const attrs =
        userData.attributes.length > 0 ? userData.attributes : ATT_DEFAULT;

      const data = {
        reading: userData.reading,
        model: userData.model,
        graph: userData.graphList,
        kind_values: KIND_DICT,
        match_values: MATCH_DICT,
        attributes: attrs,
        classify_scheme: "1",
        name_match_threshold: userData.nameMatchThreshold,
        name_sim_metric: userData.nameSimMetric,
        differ_gene_type: userData.differGeneType ?? false,
      };

      const response = await fetch(serverUrlClassify, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = `Classification failed (Status: ${response.status})`;

        try {
          const errorJson = JSON.parse(errorText);
          errorMessage =
            errorJson.detail ||
            errorJson.error ||
            errorJson.message ||
            errorMessage;
        } catch {
          if (errorText) {
            errorMessage = errorText;
          }
        }

        throw new Error(errorMessage);
      }

      const result = await response.json();

      if (!result || !result.classified_result) {
        throw new Error("Invalid classification result from server");
      }

      onClassifyComplete(result);
    } catch (error) {
      handleLoading(false);
      console.error("Error during classification:", error);

      if (
        error.name === "TypeError" &&
        error.message.includes("Failed to fetch")
      ) {
        onError(
          "Network error: Unable to connect to server. Please check your connection and try again.",
        );
      } else {
        onError(error.message || "Classification failed. Please try again.");
      }
    }
  };

  const disabled = isDisabled;
  return (
    <div
      className="button-wrapper"
      onMouseEnter={() => disabled && setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <button
        onClick={handleClassification}
        className={
          disabled ? "disabled-button classify_button" : "classify_button"
        }
        disabled={disabled}
      >
        Classify!
      </button>
      {showTooltip &&
        (isDisabled ? (
          <div className="tooltip">Please upload files first</div>
        ) : (
          <div className="tooltip">Please Choose Attributes</div>
        ))}
    </div>
  );
};

// =============================================
// CategoryButton Component
// =============================================
const CategoryButton = ({ category, userData, onToggle, isDisabled }) => {
  const [isToggled, setIsToggled] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  const handleToggle = () => {
    try {
      const newState = !isToggled;
      setIsToggled(newState);
      onToggle(category, newState);
    } catch (error) {
      console.error("Error toggling category:", error);
    }
  };

  const disabled = isDisabled || !userData.classifiedResult;

  const getButtonClass = () => {
    let baseClass = "";
    switch (category) {
      case "corroborations":
        baseClass = "corroboration_button";
        break;
      case "contradictions":
        baseClass = "contradiction_button";
        break;
      case "flagged":
        baseClass = "flagged_button";
        break;
      case "extension":
        baseClass = "extension_button";
        break;
      default:
        baseClass = "";
    }
    return `${baseClass} ${disabled ? "disabled-button" : ""}`;
  };

  const getButtonText = () => {
    return category.charAt(0).toUpperCase() + category.slice(1);
  };

  return (
    <div
      className="button-wrapper"
      onMouseEnter={() => disabled && setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <button
        className={getButtonClass()}
        onClick={handleToggle}
        disabled={disabled}
      >
        {getButtonText()}
      </button>
      {showTooltip && disabled && (
        <div className="tooltip">
          {isDisabled ? "Please upload files first" : "Please classify first"}
        </div>
      )}
    </div>
  );
};

// =============================================
// Virtualized DataTable Component (zero external deps)
// =============================================
const ROW_HEIGHT = 28;
const COL_MIN_WIDTH = 120;
const OVERSCAN = 15;

const DataTable = ({ data, userData, onRowSelect, selectedRowIndices }) => {
  const [filterText, setFilterText] = useState({});
  const [filteredData, setFilteredData] = useState(data);
  const [scrollTop, setScrollTop] = useState(0);
  const [containerHeight, setContainerHeight] = useState(0);
  const headerScrollRef = useRef(null);
  const bodyScrollRef = useRef(null);
  const resizeObserverRef = useRef(null);
  const filterTimerRef = useRef(null);

  // Reset filters when data changes
  useEffect(() => {
    try {
      setFilteredData(data);
      setFilterText({});
      setScrollTop(0);
    } catch (error) {
      console.error("Error updating table data:", error);
    }
  }, [data]);

  // Measure container height with ResizeObserver
  useEffect(() => {
    const el = bodyScrollRef.current;
    if (!el) return;

    const measure = () => setContainerHeight(el.clientHeight);
    measure();

    if (typeof ResizeObserver !== "undefined") {
      resizeObserverRef.current = new ResizeObserver(measure);
      resizeObserverRef.current.observe(el);
    }

    return () => {
      if (resizeObserverRef.current) resizeObserverRef.current.disconnect();
    };
  }, []);

  // Sync horizontal scroll between header and body
  const handleBodyScroll = useCallback((e) => {
    const target = e.currentTarget;
    setScrollTop(target.scrollTop);
    if (headerScrollRef.current) {
      headerScrollRef.current.scrollLeft = target.scrollLeft;
    }
  }, []);

  // Debounced filter
  const handleFilterChange = useCallback(
    (header, value) => {
      setFilterText((prev) => {
        const next = { ...prev, [header]: value };
        if (filterTimerRef.current) clearTimeout(filterTimerRef.current);
        filterTimerRef.current = setTimeout(() => {
          const filtered = data.filter((row) =>
            Object.keys(next).every((key) => {
              const fv = next[key]?.trim() || "";
              if (!fv) return true;

              // Numeric filter for Entity Match Score
              // Supports: <0.5, <=0.5, >0.5, >=0.5, =0.5
              // Plain number (e.g. "0.5") defaults to "less than"
              if (key === "Entity Match Score") {
                const cellVal = parseFloat(row[key]);
                if (isNaN(cellVal)) return false;
                const match = fv.match(/^(<=?|>=?|=)\s*(.+)$/);
                if (match) {
                  const op = match[1];
                  const num = parseFloat(match[2]);
                  if (isNaN(num)) return true;
                  if (op === "<") return cellVal <= num;
                  // if (op === "<=") return cellVal <= num;
                  if (op === ">") return cellVal >= num;
                  // if (op === ">=") return cellVal >= num;
                  if (op === "=") return cellVal === num;
                }
                const num = parseFloat(fv);
                if (!isNaN(num)) return cellVal < num;
                return true;
              }

              return String(row[key] || "")
                .toLowerCase()
                .includes(fv.toLowerCase());
            }),
          );
          setFilteredData(filtered);
          setScrollTop(0);
          if (bodyScrollRef.current) bodyScrollRef.current.scrollTop = 0;
        }, 300);
        return next;
      });
    },
    [data],
  );

  const clearFilters = useCallback(() => {
    setFilterText({});
    setFilteredData(data);
    setScrollTop(0);
    if (bodyScrollRef.current) bodyScrollRef.current.scrollTop = 0;
  }, [data]);

  if (!data || data.length === 0) return null;

  const excludeFields = [
    "Evidence Score",
    "Match Score",
    "Epistemic Value",
    "Total Score",
  ];

  const allHeaders = Object.keys(data[0]);
  const headers = allHeaders.filter(
    (header) => !excludeFields.includes(header.trim()),
  );

  const colWidths = headers.map((h) =>
    Math.max(COL_MIN_WIDTH, h.length * 8 + 40),
  );
  const totalRowWidth = colWidths.reduce((sum, w) => sum + w, 0);
  const totalHeight = filteredData.length * ROW_HEIGHT;

  // Calculate visible window
  const startIndex = Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - OVERSCAN);
  const visibleCount = Math.ceil(containerHeight / ROW_HEIGHT) + 2 * OVERSCAN;
  const endIndex = Math.min(filteredData.length, startIndex + visibleCount);

  const applyStyle = (value) => {
    if (value === "Corroboration") return "corroboration_marker";
    if (value === "Extension") return "extension_marker";
    if (value === "Contradiction") return "contradiction_marker";
    if (value === "Flagged") return "flagged_marker";
    return "";
  };

  const handleRowClick = (row) => {
    try {
      const originalIndex = data.indexOf(row);
      onRowSelect(row, originalIndex, allHeaders);
    } catch (error) {
      console.error("Error selecting row:", error);
    }
  };

  // Build only the visible rows
  const visibleRows = [];
  for (let i = startIndex; i < endIndex; i++) {
    const row = filteredData[i];
    const originalIndex = data.indexOf(row);
    const isSelected = selectedRowIndices.has(originalIndex);

    visibleRows.push(
      <div
        key={i}
        className={`virtual-table-row ${isSelected ? "selected" : ""}`}
        style={{
          position: "absolute",
          top: i * ROW_HEIGHT,
          height: ROW_HEIGHT,
          width: totalRowWidth,
        }}
        onClick={() => handleRowClick(row)}
      >
        {headers.map((header, colIdx) => (
          <div
            key={header}
            className={`virtual-table-cell ${applyStyle(row[header])}`}
            style={{ width: colWidths[colIdx], minWidth: colWidths[colIdx] }}
            title={String(row[header] ?? "")}
          >
            {row[header]}
          </div>
        ))}
      </div>,
    );
  }

  return (
    <div className="virtual-table-wrapper">
      {/* Toolbar */}
      <div className="virtual-table-toolbar">
        <button onClick={clearFilters} style={{ padding: "5px 10px" }}>
          Clear Filters
        </button>
        <span style={{ marginLeft: "10px", fontSize: "12px" }}>
          Showing {filteredData.length} of {data.length} rows
        </span>
      </div>

      {/* Sticky header */}
      <div className="virtual-table-header-scroll" ref={headerScrollRef}>
        <div
          className="virtual-table-header-row"
          style={{ width: totalRowWidth }}
        >
          {headers.map((header, colIdx) => (
            <div
              key={header}
              className="virtual-table-header-cell"
              style={{ width: colWidths[colIdx], minWidth: colWidths[colIdx] }}
            >
              <span>{header}</span>
              <input
                type="text"
                placeholder={
                  header === "Entity Match Score"
                    ? "e.g. <0.5, >0.5, =0.5"
                    : "Filter..."
                }
                value={filterText[header] || ""}
                onChange={(e) => handleFilterChange(header, e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Virtualized body */}
      <div
        className="virtual-table-body-scroll"
        ref={bodyScrollRef}
        onScroll={handleBodyScroll}
        style={{ overflowY: "auto", overflowX: "auto" }}
      >
        <div
          style={{
            position: "relative",
            height: totalHeight,
            width: totalRowWidth,
          }}
        >
          {visibleRows}
        </div>
      </div>
    </div>
  );
};

// =============================================
// DownloadButton Component
// =============================================
const DownloadButton = ({
  data,
  filename = "violin_classification_results.xlsx",
  onError,
  loadingModel,
  loadingReading,
}) => {
  const [showTooltip, setShowTooltip] = useState(false);

  const handleDownload = () => {
    try {
      if (!data || data.length === 0) {
        onError("No data to download. Please classify first.");
        return;
      }

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(data);

      const cols = Object.keys(data[0]).map((key) => ({
        wch: Math.max(key.length, 15),
      }));
      ws["!cols"] = cols;

      XLSX.utils.book_append_sheet(wb, ws, "All Results");

      const categories = [
        "Corroboration",
        "Contradiction",
        "Extension",
        "Flagged",
      ];
      categories.forEach((category) => {
        const categoryData = data.filter((row) => row.Category === category);
        if (categoryData.length > 0) {
          const categoryWs = XLSX.utils.json_to_sheet(categoryData);
          categoryWs["!cols"] = cols;
          XLSX.utils.book_append_sheet(wb, categoryWs, category);
        }
      });

      XLSX.writeFile(wb, filename);
    } catch (error) {
      console.error("Error downloading file:", error);
      onError("Failed to download file. Please try again.");
    }
  };

  const disabled = !data || data.length === 0 || loadingReading || loadingModel;

  return (
    <div
      className="button-wrapper"
      onMouseEnter={() => disabled && setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <button
        onClick={handleDownload}
        className={
          disabled ? "disabled-button download_button" : "download_button"
        }
        disabled={disabled}
      >
        <div style={{ marginRight: "4px" }}>Download Results</div>
        <Img src={download} />
      </button>
      {showTooltip && disabled && (
        <div className="tooltip tooltip-download-button">
          Please classify first to download results
        </div>
      )}
    </div>
  );
};

// =============================================
// Reset Button Component
// =============================================
const ResetButton = ({ userData, onReset, isDisabled }) => {
  const [showTooltip, setShowTooltip] = useState(false);

  const handleReset = () => {
    try {
      if (
        window.confirm(
          "Are you sure you want to reset? This will clear all classification results and return to the initial state.",
        )
      ) {
        onReset();
      }
    } catch (error) {
      console.error("Error during reset:", error);
    }
  };

  const disabled = isDisabled || !userData.classifiedResult;

  return (
    <div
      className="button-wrapper"
      onMouseEnter={() => disabled && setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <button
        onClick={handleReset}
        className={disabled ? "disabled-button reset_button" : "reset_button"}
        disabled={disabled}
      >
        Reset
      </button>
      {showTooltip && disabled && (
        <div className="tooltip">
          {isDisabled ? "Please upload files first" : "Please classify first"}
        </div>
      )}
    </div>
  );
};

// =============================================
// InteractionDisplay Component
// =============================================
const InteractionDisplay = ({ data }) => {
  if (!data) return null;

  const getCategoryMarker = (category) => {
    const markerClass = {
      Corroboration: "corroboration_marker",
      Extension: "extension_marker",
      Contradiction: "contradiction_marker",
      Flagged: "flagged_marker",
    }[category];

    return <mark className={markerClass}>{category}</mark>;
  };

  const getCircleClass = (category) => {
    return (
      {
        Corroboration: "circle-corroboration",
        Extension: "circle-extension",
        Contradiction: "circle-contradiction",
        Flagged: "circle-flagged",
      }[category] || ""
    );
  };

  return (
    <div id="interaction_info">
      {data.category && (
        <p id="entity">Category: {getCategoryMarker(data.category)}</p>
      )}
      {data.subcategory && <p id="entity">Sub-category: {data.subcategory}</p>}

      <p style={{ marginTop: "1em" }}>
        <span id="entity">Regulator:</span> {data.regulatorName || ""} | ID(
        {data.regulatorDatabase || ""}
        ): {data.regulatorName || ""} | Type: {data.regulatorType || ""}
      </p>

      <p>
        <span id="entity">Regulated:</span> {data.regulatedName || ""} | ID(
        {data.regulatedDatabase || ""}
        ): {data.regulatedId || ""} | Type: {data.regulatedType || ""}
      </p>

      <p>
        <span id="entity">Interaction:</span>
        {data.sign === "positive" ? " +" : " -"} | Type:{" "}
        {data.connectionType === "i"
          ? "indirect"
          : data.connectionType === "d"
            ? "direct"
            : ""}{" "}
        | Mechanism: {data.mechanism} | Site: {data.mechanism}
      </p>
      {data.showVisualization && (
        <div id="visualization">
          <span className={`circle ${getCircleClass(data.category)}`}>
            {data.regulatorName}
          </span>
          <span>{data.sign === "positive" ? "-->" : "--|"}</span>
          <span className={`circle ${getCircleClass(data.category)}`}>
            {data.regulatedName}
          </span>
        </div>
      )}
      <p id="entity" style={{ marginTop: "1em" }}>
        Context:
      </p>
      <p>
        Cell Line: {data.cellLine || ""} | Cell Type: {data.cellType || ""}
      </p>
      <p>
        Tissue Type: {data.tissueType || ""} | Organism: {data.organism || ""}
      </p>
      <p id="entity" style={{ marginTop: "1em" }}>
        Provenance:
      </p>
      <p>
        Paper ID:{" "}
        {data.paperId?.length > 0 && (
          <a
            href={`https://pubmed.ncbi.nlm.nih.gov/${data.paperId[0]}/`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "#0066cc", textDecoration: "underline" }}
          >
            {data.paperId}
          </a>
        )}
      </p>
      <p>Evidence: {data.statements}</p>
    </div>
  );
};

// =============================================
// Main VIOLIN Component
// =============================================
const GraphLegend = ({ model }) => {
  const types = entityTypesInModel(model);
  if (!types.length) return null;
  return (
    <div
      style={{
        position: "absolute",
        top: 10,
        right: 10,
        zIndex: 5,
        background: "rgba(255, 255, 255, 0.92)",
        border: "1px solid #ddd",
        borderRadius: 8,
        padding: "8px 10px",
        fontSize: 12,
        boxShadow: "0 2px 6px rgba(0, 0, 0, 0.12)",
        maxWidth: 200,
        maxHeight: "60%",
        overflowY: "auto",
      }}
    >
      <div style={{ fontWeight: "bold", marginBottom: 6, color: "#333" }}>
        Entity type
      </div>
      {types.map((t) => (
        <div
          key={t}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            margin: "3px 0",
          }}
        >
          <span
            style={{
              width: 12,
              height: 12,
              borderRadius: "50%",
              background: nodeColor(t),
              display: "inline-block",
              flex: "0 0 auto",
            }}
          />
          <span style={{ color: "#333" }}>{t}</span>
        </div>
      ))}
    </div>
  );
};

const ViolinApp = () => {
  const [userData, setUserData] = useState({
    reading: null,
    model: null,
    graphList: null,
    attributes: [],
    nameMatchThreshold: null,
    nameSimMetric: "none",
    differGeneType: true,
    classifiedResult: null,
    corroborationEdges: null,
    contradictionEdges: null,
    flaggedEdges: null,
    extensionEdges: null,
    cy: null,
  });

  const [tableData, setTableData] = useState(null);
  const [interactionInfo, setInteractionInfo] = useState(null);
  const [loadingReading, setLoadingReading] = useState(false);
  const [loadingModel, setLoadingModel] = useState(false);
  const [file, setFile] = useState(null);
  const [graphKey, setGraphKey] = useState(0);
  const [error, setError] = useState(null);
  const [selectedRowIndices, setSelectedRowIndices] = useState(new Set());
  const [isClassifying, setIsClassifying] = useState(false);

  const graphContainerRef = useRef(null);
  const destroyingRef = useRef(false);

  const handleError = (errorMessage) => {
    setError(errorMessage);
    console.error("Application Error:", errorMessage);
  };

  const clearError = () => {
    setError(null);
  };
  //For clearing interaction info
  useEffect(() => {
    if (loadingReading) {
      setInteractionInfo(null);
    }
  }, [loadingReading]);
  useEffect(() => {
    if (loadingModel) {
      setInteractionInfo(null);
    }
  }, [loadingModel]);
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.userData = userData;
    }
  }, [userData]);

  useEffect(() => {
    if (
      userData.model &&
      userData.graphList &&
      !userData.cy &&
      graphContainerRef.current &&
      !destroyingRef.current
    ) {
      try {
        const nodes_and_edges = [];

        for (let i = 0; i < userData.model.length; i++) {
          const element = userData.model[i]["Element Name"];
          const Variable = userData.model[i]["Variable"];
          const element_type = userData.model[i]["Element Type"];
          nodes_and_edges.push({
            data: {
              id: Variable,
              name: element,
              type: element_type,
            },
          });
        }

        for (const src in userData.graphList) {
          for (const tgt in userData.graphList[src]) {
            nodes_and_edges.push({
              data: {
                id: `${src} -> ${tgt}`,
                source: src,
                target: tgt,
                arrow:
                  userData.graphList[src][tgt].weight === 1
                    ? "tee"
                    : "triangle-backcurve",
              },
            });
          }
        }

        const graph_style = [
          {
            selector: "node",
            style: {
              "background-color": (ele) => nodeColor(ele.data("type")),
              label: "data(name)",
            },
          },
          {
            selector: "edge[arrow]",
            style: {
              width: 3,
              "line-color": "#ccc",
              "target-arrow-color": "#ccc",
              "target-arrow-shape": "data(arrow)",
              "target-endpoint": "outside-to-node",
              "curve-style": "bezier",
            },
          },
        ];

        const cy = cytoscape({
          container: graphContainerRef.current,
          elements: nodes_and_edges,
          style: graph_style,
          layout: {
            name: "random",
          },
        });

        setUserData((prev) => ({ ...prev, cy }));
        if (typeof window !== "undefined") {
          window.userData.cy = cy;
        }
      } catch (error) {
        console.error("Error initializing graph:", error);
        handleError(
          "Failed to initialize graph visualization. Please try uploading the model file again.",
        );
      }
    }
  }, [userData.model, userData.graphList, userData.cy]);

  const handleUploadComplete = (result, postAccess) => {
    try {
      if (postAccess === "upload_violin_reading") {
        if (!result.dataframe || !Array.isArray(result.dataframe)) {
          throw new Error("Invalid reading file format");
        }
        setUserData((prev) => ({
          ...prev,
          reading: result.dataframe,
        }));
        setTableData(result.dataframe);
        setSelectedRowIndices(new Set());
        setLoadingReading(false);
      } else if (postAccess === "upload_violin_model") {
        if (!result.dataframe || !result.graph_list) {
          throw new Error("Invalid model file format");
        }

        destroyingRef.current = true;

        if (userData.cy) {
          try {
            userData.cy.destroy();
          } catch (error) {
            console.warn("Error destroying Cytoscape instance:", error);
          }
        }

        if (graphContainerRef.current) {
          while (graphContainerRef.current.firstChild) {
            graphContainerRef.current.removeChild(
              graphContainerRef.current.firstChild,
            );
          }
        }

        setTimeout(() => {
          destroyingRef.current = false;
          setGraphKey((prev) => prev + 1);
          setUserData((prev) => ({
            ...prev,
            model: result.dataframe,
            graphList: result.graph_list,
            cy: null,
          }));
          setLoadingModel(false);
        }, 100);
      }
    } catch (error) {
      console.error("Error processing upload:", error);
      handleError(error.message || "Failed to process uploaded file");
      setLoadingReading(false);
      setLoadingModel(false);
    }
  };

  const handleClassifyComplete = (result) => {
    try {
      if (
        !result.classified_result ||
        !Array.isArray(result.classified_result)
      ) {
        throw new Error("Invalid classification result");
      }

      const enrichedData = result.classified_result.map((row) => {
        const kindScore = row["Kind Score"];
        const subcategory = REVERSE_KIND_DICT[String(kindScore)];

        let category = "";

        if (
          [
            "strong corroboration",
            "empty attribute",
            "indirect interaction",
            "path corroboration",
            "specification",
          ].includes(subcategory)
        ) {
          category = "Corroboration";
        } else if (
          [
            "hanging extension",
            "full extension",
            "internal extension",
          ].includes(subcategory)
        ) {
          category = "Extension";
        } else if (
          [
            "dir contradiction",
            "sign contradiction",
            "att contradiction",
          ].includes(subcategory)
        ) {
          category = "Contradiction";
        } else {
          category = "Flagged";
        }

        return {
          Category: category,
          "Sub-category": subcategory,
          ...row,
        };
      });

      setUserData((prev) => ({
        ...prev,
        classifiedResult: enrichedData,
        corroborationEdges: result.corroboration_edges,
        contradictionEdges: result.contradiction_edges,
        flaggedEdges: result.flagged_edges,
        extensionEdges: result.extension_edges,
      }));

      setTableData(enrichedData);
      setSelectedRowIndices(new Set());

      if (typeof window !== "undefined") {
        window.userData = {
          ...window.userData,
          classifiedResult: enrichedData,
          corroborationEdges: result.corroboration_edges,
          contradictionEdges: result.contradiction_edges,
          flaggedEdges: result.flagged_edges,
          extensionEdges: result.extension_edges,
        };
      }
      setLoadingReading(false);
    } catch (error) {
      console.error("Error processing classification results:", error);
      handleError("Failed to process classification results");
      setLoadingReading(false);
    }
  };

  const handleReset = () => {
    try {
      if (userData.cy && userData.model && userData.graphList) {
        userData.cy.elements().remove();

        const nodes_and_edges = [];

        for (let i = 0; i < userData.model.length; i++) {
          const element = userData.model[i]["Element Name"];
          const Variable = userData.model[i]["Variable"];
          const element_type = userData.model[i]["Element Type"];
          nodes_and_edges.push({
            data: {
              id: Variable,
              name: element,
              type: element_type,
            },
          });
        }

        for (const src in userData.graphList) {
          for (const tgt in userData.graphList[src]) {
            nodes_and_edges.push({
              data: {
                id: `${src} -> ${tgt}`,
                source: src,
                target: tgt,
                arrow:
                  userData.graphList[src][tgt].weight === 1
                    ? "tee"
                    : "triangle-backcurve",
              },
            });
          }
        }

        userData.cy.add(nodes_and_edges);

        const graph_style = [
          {
            selector: "node",
            style: {
              "background-color": (ele) => nodeColor(ele.data("type")),
              label: "data(name)",
              "border-width": "0px",
            },
          },
          {
            selector: "edge[arrow]",
            style: {
              width: 3,
              "line-color": "#ccc",
              "target-arrow-color": "#ccc",
              "target-arrow-shape": "data(arrow)",
              "target-endpoint": "outside-to-node",
              "curve-style": "bezier",
              "line-style": "solid",
            },
          },
        ];

        userData.cy.style(graph_style);
        userData.cy.layout({ name: "random" }).run();
      }

      setTableData(userData.reading);
      setInteractionInfo(null);
      setSelectedRowIndices(new Set());

      setUserData((prev) => ({
        ...prev,
        classifiedResult: null,
        corroborationEdges: null,
        contradictionEdges: null,
        flaggedEdges: null,
        extensionEdges: null,
      }));

      if (typeof window !== "undefined") {
        window.userData = {
          ...window.userData,
          classifiedResult: null,
          corroborationEdges: null,
          contradictionEdges: null,
          flaggedEdges: null,
          extensionEdges: null,
        };
      }
    } catch (error) {
      console.error("Error during reset:", error);
      handleError("Failed to reset. Please try again.");
    }
  };

  const handleAttributeChange = (attributes, nameMatchConfig) => {
    try {
      setUserData((prev) => ({
        ...prev,
        attributes,
        nameMatchThreshold:
          nameMatchConfig?.name_match_threshold ?? prev.nameMatchThreshold,
        nameSimMetric: nameMatchConfig?.name_sim_metric ?? prev.nameSimMetric,
        differGeneType:
          nameMatchConfig?.differ_gene_type ?? prev.differGeneType,
      }));
      if (typeof window !== "undefined") {
        window.userData.attributes = attributes;
        window.userData.nameMatchThreshold =
          nameMatchConfig?.name_match_threshold;
        window.userData.nameSimMetric = nameMatchConfig?.name_sim_metric;
        window.userData.differGeneType = nameMatchConfig?.differ_gene_type;
      }
    } catch (error) {
      console.error("Error changing attributes:", error);
    }
  };

  const handleCategoryToggle = (category, isActive) => {
    try {
      if (!userData.cy || !userData.model || !userData.graphList) return;

      if (!isActive) {
        const nodes_and_edges = [];

        for (let i = 0; i < userData.model.length; i++) {
          const element = userData.model[i]["Element Name"];
          const Variable = userData.model[i]["Variable"];
          const element_type = userData.model[i]["Element Type"];
          nodes_and_edges.push({
            data: {
              id: Variable,
              name: element,
              type: element_type,
            },
          });
        }

        for (const src in userData.graphList) {
          for (const tgt in userData.graphList[src]) {
            nodes_and_edges.push({
              data: {
                id: `${src} -> ${tgt}`,
                source: src,
                target: tgt,
                arrow:
                  userData.graphList[src][tgt].weight === 1
                    ? "tee"
                    : "triangle-backcurve",
              },
            });
          }
        }

        const graph_style = [
          {
            selector: "node",
            style: {
              "background-color": (ele) => nodeColor(ele.data("type")),
              label: "data(name)",
            },
          },
          {
            selector: "edge[arrow]",
            style: {
              width: 3,
              "line-color": "#ccc",
              "target-arrow-color": "#ccc",
              "target-arrow-shape": "data(arrow)",
              "target-endpoint": "outside-to-node",
              "curve-style": "bezier",
            },
          },
        ];

        userData.cy.style(graph_style);
        return;
      }

      let edges, color;

      switch (category) {
        case "corroborations":
          edges = userData.corroborationEdges;
          color = "#c8daf7";
          break;
        case "contradictions":
          edges = userData.contradictionEdges;
          color = "#fdeeb4";
          break;
        case "flagged":
          edges = userData.flaggedEdges;
          color = "#ee827d";
          break;
        case "extension":
          edges = userData.extensionEdges;
          color = "#cbe3c4";
          break;
        default:
          return;
      }

      if (!edges) return;

      for (let i = 0; i < edges.length; i++) {
        const target_id = userData.model[edges[i]["target_index"]].Variable;
        const source_id = userData.model[edges[i]["source_index"]].Variable;
        const edge_index = `${source_id} -> ${target_id}`;
        if (userData.model[edges[i]["kind_score"]] === "5") continue;

        userData.cy
          .style()
          .selector(`#${target_id}, #${source_id}`)
          .style("background-color", color)
          .update();

        userData.cy
          .style()
          .selector(`edge[id = "${edge_index}"]`)
          .style({ "line-color": color, "target-arrow-color": color })
          .update();
      }
    } catch (error) {
      console.error("Error toggling category:", error);
      handleError("Failed to toggle category visualization");
    }
  };

  const getPath = async (pathData) => {
    try {
      const response = await fetch(`${API_BASE}/find_path/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(pathData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = `Failed to find path (Status: ${response.status})`;

        try {
          const errorJson = JSON.parse(errorText);
          errorMessage =
            errorJson.detail ||
            errorJson.error ||
            errorJson.message ||
            errorMessage;
        } catch {
          if (errorText) {
            errorMessage = errorText;
          }
        }

        throw new Error(errorMessage);
      }

      const pathResult = await response.json();

      if (!pathResult.path || !Array.isArray(pathResult.path)) {
        throw new Error("Invalid path result");
      }

      const pathEdges = [];
      for (let i = 0; i < pathResult.path.length - 1; i++) {
        pathEdges.push(`${pathResult.path[i]} -> ${pathResult.path[i + 1]}`);
      }

      const pathNodes = [];
      for (let i = 0; i < pathResult.path.length; i++) {
        pathNodes.push(`#${pathResult.path[i]}`);
      }

      return { pathEdges, pathNodes };
    } catch (error) {
      console.error("Error finding path:", error);
      return { pathEdges: [], pathNodes: [] };
    }
  };

  const joinPath = (pathEdges, pathNodes, score) => {
    try {
      let color = "grey";
      let borderColor = "grey";
      if (score === "path corroboration") {
        color = "#c8daf7";
        borderColor = "#1463f7";
      } else if (score === "path mismatch") {
        color = "#ee827d";
        borderColor = "#FF073A";
      } else if (score === "internal extension") {
        color = "#8dd976";
        borderColor = "#0f4d04";
      }

      if (!userData.cy) return;

      for (let i = 0; i < pathNodes.length; i++) {
        userData.cy
          .style()
          .selector(`${pathNodes[i]}`)
          .style({
            "background-color": color,
            "border-width": "5px",
            "border-color": borderColor,
            "border-style": "solid",
          })
          .update();
      }

      for (let i = 0; i < pathEdges.length; i++) {
        userData.cy
          .style()
          .selector(`edge[id="${pathEdges[i]}"]`)
          .style({
            "line-color": borderColor,
            "target-arrow-color": color,
            "border-width": "5px",
            "border-color": borderColor,
            "border-style": "solid",
          })
          .update();
      }
    } catch (error) {
      console.error("Error joining path:", error);
    }
  };

  const joinInteraction = (source, target, sign, cat, subcategory = "") => {
    try {
      if (!userData.cy) return;

      let color = "grey";
      let borderColor = "grey";

      const arrow_shape = sign === "positive" ? "triangle-backcurve" : "tee";

      if (cat === "corroboration") {
        color = "#c8daf7";
        borderColor = "#1463f7";
      } else if (cat === "contradiction") {
        color = "#fdeeb4";
        borderColor = "#ff8000";
      } else if (cat === "flagged") {
        color = "#ee827d";
        borderColor = "#FF073A";
      } else if (cat === "extension") {
        color = "#cbe3c4";
        borderColor = "#0f4d04";

        if (!userData.cy.$id(source).length) {
          userData.cy.add({
            group: "nodes",
            data: { id: source, name: source },
            position: {
              x: Math.random() * 800,
              y: Math.random() * 600,
            },
          });
        }
        if (!userData.cy.$id(target).length) {
          userData.cy.add({
            group: "nodes",
            data: { id: target, name: target },
            position: {
              x: Math.random() * 800,
              y: Math.random() * 600,
            },
          });
        }
      }

      userData.cy.$id(source).style({
        "background-color": color,
        "border-width": "5px",
        "border-color": borderColor,
        "border-style": "solid",
      });
      userData.cy.$id(target).style({
        "background-color": color,
        "border-width": "5px",
        "border-color": borderColor,
        "border-style": "solid",
      });

      const edgeId = `${source} -> ${target} (${cat})`;

      if (!userData.cy.$id(edgeId).length) {
        userData.cy.add({
          group: "edges",
          data: { id: edgeId, source: source, target: target },
        });
      }
      if (
        subcategory === "strong corroboration" ||
        subcategory === "indirect interaction"
      ) {
        userData.cy.$id(edgeId).style({
          "line-color": borderColor,
          "target-arrow-color": borderColor,
          "curve-style": "bezier",
          "target-arrow-shape": arrow_shape,
        });
      } else {
        userData.cy.$id(edgeId).style({
          "line-color": borderColor,
          "target-arrow-color": borderColor,
          "line-style": "dashed",
          "curve-style": "bezier",
          "target-arrow-shape": arrow_shape,
        });
      }
    } catch (error) {
      console.error("Error joining interaction:", error);
    }
  };

  const handleRowSelect = async (row, rowIndex, headers) => {
    try {
      setSelectedRowIndices((prev) => {
        const newSet = new Set(prev);
        newSet.add(rowIndex);
        return newSet;
      });
      const baseInfo = {
        regulatorName: row["Regulator Name"],
        regulatorDatabase: row["Regulator Database"],
        regulatorType: row["Regulator Type"],
        regulatedName: row["Regulated Name"],
        regulatedDatabase: row["Regulated Database"],
        regulatedId: row["Regulated ID"],
        regulatedType: row["Regulated Type"],
        cellLine: row["Cell Line"],
        cellType: row["Cell Type"],
        tissueType: row["Tissue Type"],
        organism: row["Organism"],
        paperId: row["Paper IDs"],
        statements: row["Statements"],
        sign: row["Sign"],
        mechanism: row["Mechanism"],
        showVisualization: false,
        category: null,
        subcategory: null,
        connectionType: row["Connection Type"],
      };

      if (headers.includes("Kind Score")) {
        const subcategory = REVERSE_KIND_DICT[String(row["Kind Score"])];
        let category = "";
        let pathEdges, pathNodes;

        if (["path corroboration", "path mismatch"].includes(subcategory)) {
          let interaction = null;
          if (subcategory === "path corroboration") {
            interaction = userData.corroborationEdges?.find(
              (i) => i.interaction_index === rowIndex,
            );
          } else if (subcategory === "path mismatch") {
            interaction = userData.flaggedEdges?.find(
              (i) => i.interaction_index === rowIndex,
            );
          }
          if (
            interaction &&
            userData.model[interaction?.source_index] &&
            userData.model[interaction?.target_index]
          ) {
            const pathData = {
              regulator: userData.model[interaction.source_index].Variable,
              regulated: userData.model[interaction.target_index].Variable,
              model: userData.model,
              graph: userData.graphList,
              kind_score: row["Kind Score"],
            };
            const result = await getPath(pathData);
            pathEdges = result.pathEdges;
            pathNodes = result.pathNodes;
          }
        }

        if (
          [
            "strong corroboration",
            "empty attribute",
            "indirect interaction",
            "path corroboration",
            "specification",
          ].includes(subcategory)
        ) {
          category = "Corroboration";
          baseInfo.showVisualization = true;

          if (subcategory === "path corroboration") {
            if (pathEdges && pathNodes) {
              joinPath(pathEdges, pathNodes, "path corroboration");
            }
          } else {
            const interaction = userData.corroborationEdges?.find(
              (i) => i.interaction_index === rowIndex,
            );
            if (
              interaction &&
              userData.model[interaction?.target_index] &&
              userData.model[interaction?.source_index]
            ) {
              const target = userData.model[interaction.target_index].Variable;
              const source = userData.model[interaction.source_index].Variable;

              joinInteraction(
                source,
                target,
                row["Sign"],
                "corroboration",
                subcategory,
              );
            }
          }
        } else if (
          [
            "hanging extension",
            "full extension",
            "internal extension",
          ].includes(subcategory)
        ) {
          category = "Extension";
          baseInfo.showVisualization = true;

          if (subcategory === "full extension") {
            const interaction = userData.extensionEdges?.find(
              (i) => i.interaction_index === rowIndex,
            );
            if (interaction) {
              const target = `${row["Regulated Name"]}_reading`;
              const source = `${row["Regulator Name"]}_reading`;
              joinInteraction(
                source,
                target,
                row["Sign"],
                "extension",
                subcategory,
              );
            }
          } else if (subcategory === "hanging extension") {
            const interaction = userData.extensionEdges?.find(
              (i) => i.interaction_index === rowIndex,
            );
            if (interaction) {
              if (
                interaction.source_index === "" ||
                interaction.source_index === null ||
                interaction.source_index === undefined
              ) {
                const source = `${row["Regulator Name"]}_reading`;
                const target =
                  userData.model[interaction.target_index]?.["Variable"];
                if (target) {
                  joinInteraction(
                    source,
                    target,
                    row["Sign"],
                    "extension",
                    subcategory,
                  );
                }
              } else {
                const target = `${row["Regulated Name"]}_reading`;
                const source =
                  userData.model[interaction.source_index]?.["Variable"];
                if (source) {
                  joinInteraction(
                    source,
                    target,
                    row["Sign"],
                    "extension",
                    subcategory,
                  );
                }
              }
            }
          } else {
            const interaction = userData.extensionEdges?.find(
              (i) => i.interaction_index === rowIndex,
            );
            if (interaction) {
              const target = userData.model[interaction.target_index].Variable;
              const source = userData.model[interaction.source_index].Variable;
              joinInteraction(
                source,
                target,
                row["Sign"],
                "extension",
                subcategory,
              );
            }
          }
        } else if (
          [
            "dir contradiction",
            "sign contradiction",
            "att contradiction",
          ].includes(subcategory)
        ) {
          category = "Contradiction";
          baseInfo.showVisualization = true;

          const interaction = userData.contradictionEdges?.find(
            (i) => i.interaction_index === rowIndex,
          );
          if (
            interaction &&
            userData.model[interaction?.target_index] &&
            userData.model[interaction?.source_index]
          ) {
            const target = userData.model[interaction.target_index]["Variable"];
            const source = userData.model[interaction.source_index]["Variable"];
            joinInteraction(
              source,
              target,
              row["Sign"],
              "contradiction",
              subcategory,
            );
          }
        } else {
          category = "Flagged";
          baseInfo.showVisualization = true;

          if (subcategory === "path mismatch") {
            if (pathEdges && pathNodes) {
              joinPath(pathEdges, pathNodes, "path mismatch");
            }
          } else {
            const interaction = userData.flaggedEdges?.find(
              (i) => i.interaction_index === rowIndex,
            );
            if (
              interaction &&
              userData.model[interaction?.target_index] &&
              userData.model[interaction?.source_index]
            ) {
              const target =
                userData.model[interaction.target_index]["Variable"];
              const source =
                userData.model[interaction.source_index]["Variable"];
              joinInteraction(
                source,
                target,
                row["Sign"],
                "flagged",
                subcategory,
              );
            }
          }
        }

        baseInfo.category = category;
        baseInfo.subcategory = subcategory;
      }

      setInteractionInfo(baseInfo);
    } catch (error) {
      console.error("Error selecting row:", error);
      handleError("Failed to display interaction details");
    }
  };

  return (
    <>
      <style>{styles}</style>
      <ErrorBanner error={error} onClose={clearError} />
      <div className="web_container">
        {/* <Header
          fromTool="violin"
          isToolPage
          gLink="https://github.com/pitt-miskov-zivanov-lab/VIOLIN"
          rddLink=""
        /> */}
        <div className="main-content">
          <div className="left_container">
            <div>
              <UploadButton
                postAccess="upload_violin_reading"
                onUploadComplete={handleUploadComplete}
                handleLoading={(loading) => setLoadingReading(loading)}
                file={file}
                setFile={setFile}
                onError={handleError}
                hasBioLink
                title={"Upload New Interactions"}
                sampleFilePath={interactionSampleFile} // ← NEW
              />
            </div>
            <div className="table_window">
              {loadingReading ? (
                <div className="loading">
                  <ReactLoading type={"balls"} color={"#ccc"} />
                  <p
                    style={{
                      color: "#888",
                      fontSize: "14px",
                      marginTop: "10px",
                    }}
                  >
                    {isClassifying
                      ? "Classifying... this may take some time"
                      : "Uploading... this may take some time"}
                  </p>
                </div>
              ) : tableData ? (
                <DataTable
                  data={tableData}
                  userData={userData}
                  onRowSelect={handleRowSelect}
                  selectedRowIndices={selectedRowIndices}
                />
              ) : (
                <div className="empty-state">
                  <div className="empty-state-icon">📄</div>
                  <p className="empty-state-title">No interactions uploaded</p>
                  <p className="empty-state-subtitle">
                    Upload a new interactions file (.xlsx) to view and classify
                    interactions
                  </p>
                </div>
              )}
            </div>
            <div className="controller_container">
              <div className="interaction_window">
                {interactionInfo ? (
                  <InteractionDisplay data={interactionInfo} />
                ) : (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "100%",
                      color: "#888",
                      fontSize: "16px",
                      fontStyle: "italic",
                    }}
                  >
                    Select a row from interaction list to view details
                  </div>
                )}
              </div>
              <div className="controller_window">
                <div>
                  <ChooseAttr
                    onAttributeChange={handleAttributeChange}
                    isDisabled={
                      !(userData.model && userData.reading) ||
                      loadingReading ||
                      loadingModel
                    }
                  />
                  <p></p>
                </div>
                <div>
                  <ClassificationButton
                    userData={userData}
                    onClassifyComplete={handleClassifyComplete}
                    isDisabled={
                      !(userData.model && userData.reading) ||
                      loadingReading ||
                      loadingModel
                    }
                    handleLoading={(loading) => {
                      setIsClassifying(loading);
                      setLoadingReading(loading);
                    }}
                    onError={handleError}
                  />
                  <p></p>
                  <div style={{ display: "flex", gap: "10px" }}>
                    <CategoryButton
                      category="corroborations"
                      userData={userData}
                      onToggle={handleCategoryToggle}
                      isDisabled={
                        !(userData.model && userData.reading) ||
                        loadingReading ||
                        loadingModel
                      }
                    />
                    <CategoryButton
                      category="contradictions"
                      userData={userData}
                      onToggle={handleCategoryToggle}
                      isDisabled={
                        !(userData.model && userData.reading) ||
                        loadingReading ||
                        loadingModel
                      }
                    />
                  </div>
                  <p></p>
                </div>
                <div style={{ display: "flex", gap: "10px" }}>
                  <DownloadButton
                    data={userData.classifiedResult}
                    filename={`${file?.name?.replace(/\.[^/.]+$/, "")}_violin_classification_results.xlsx`}
                    onError={handleError}
                    loadingModel={loadingModel}
                    loadingReading={loadingReading}
                  />

                  <ResetButton
                    userData={userData}
                    onReset={handleReset}
                    isDisabled={
                      !(userData.model && userData.reading) ||
                      loadingReading ||
                      loadingModel
                    }
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="graph_container">
            <div>
              <UploadButton
                postAccess="upload_violin_model"
                onUploadComplete={handleUploadComplete}
                handleLoading={(loading) => setLoadingModel(loading)}
                file={file}
                setFile={setFile}
                onError={handleError}
                title={"Upload Baseline Model"}
                sampleFilePath={modelSampleFile}
              />
            </div>
            <div
              id="graphContainer"
              className="graph_window"
              ref={graphContainerRef}
              key={graphKey}
            >
              {userData.model && !loadingModel && (
                <GraphLegend model={userData.model} />
              )}
              {loadingModel ? (
                <div className="loading">
                  <ReactLoading type={"balls"} color={"#ccc"} />
                  <p
                    style={{
                      color: "#888",
                      fontSize: "14px",
                      marginTop: "10px",
                    }}
                  >
                    Uploading... this may take some time
                  </p>
                </div>
              ) : (
                !userData.model && (
                  <div className="empty-state">
                    <div className="empty-state-icon">📊</div>
                    <p className="empty-state-title">
                      No baseline model uploaded
                    </p>
                    <p className="empty-state-subtitle">
                      Upload a baseline model file (.xlsx) to visualize the
                      interaction graph
                    </p>
                  </div>
                )
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ViolinApp;
